/**
 * Klaviyo events and profile properties built on one variant-level object
 * (snippets/tracking-variant-json.liquid), so flows read the product from the event or
 * profile instead of a catalog lookup that can fail and skip the email.
 *
 * Events: Viewed Product (tagged Trigger: page_view or variant_change), Variant Selected,
 * and the custom metrics LDC - Cart Snapshot,
 * LDC - Added to Wishlist, LDC - Read Buying Guide, LDC - Viewed Trade Program.
 * Profile: Cart, Recently Viewed, brand views, Wishlist, guide reads, trade status, and
 * room previews when the theme setting allows it.
 *
 * klaviyo-enhanced.liquid owns identity and the anonymous queue. This file starts on its
 * `klaviyo-enhanced:ready` event and reads identity from window.KlaviyoQueue. Page data
 * comes from the #KlaviyoTrackingData JSON block rendered there, which carries only the
 * selected variant. Any other variant, and the cart lines, come from the
 * klaviyo-tracking-data section. Nothing reaches Klaviyo, and nothing is kept on the
 * device, when the shopper declined analytics.
 */
(() => {
  const dataEl = document.getElementById('KlaviyoTrackingData');
  if (!dataEl) return;

  let page;
  try {
    page = JSON.parse(dataEl.textContent);
  } catch {
    return;
  }

  const STATE_KEY = 'klaviyo_profile_state';
  const SECTION_ID = 'klaviyo-tracking-data';
  const VIEWED_PRODUCT_DELAY = 1000;
  const VARIANT_CHANGE_DELAY = 1000;
  const CART_SNAPSHOT_DELAY = 2000;
  const WISHLIST_SYNC_DELAY = 500;
  const GUIDE_READ_DELAY = 30000;
  const RECENTLY_VIEWED_MAX = 5;
  const RECENT_BRANDS_MAX = 5;
  const GUIDES_READ_MAX = 10;
  const WISHLIST_MAX = 50;
  const SECTION_FETCHES_AT_ONCE = 4;
  const ROOM_PREVIEWS_MAX = 5;
  // room-studio.js deletes previews after 30 days, and the consent copy promises it.
  const ROOM_PREVIEW_LIFETIME = 30 * 24 * 60 * 60 * 1000;
  const CART_VALUE_WINDOW = 7 * 24 * 60 * 60 * 1000;
  // The catalog files downrods, canopies, controls, glass and fan light kits under an
  // "... Accessories" step, and shades as "Lamps > Shades". Words like "glass" or "light
  // kit" alone also match main products: "Track Light Kits" is a whole track system.
  const ACCESSORY_PATTERN = /accessor|bulb|> shades$/i;

  const tracking = page.tracking || {};
  const rootUrl = window.Shopify?.routes?.root || '/';

  const now = () => new Date().toISOString();
  const unique = (list) => [...new Set(list.filter(Boolean))];

  // ---------------------------------------------------------------------------
  // State kept on the device, written to the profile once the shopper is known
  // ---------------------------------------------------------------------------

  function loadState() {
    try {
      return JSON.parse(localStorage.getItem(STATE_KEY)) || {};
    } catch {
      return {};
    }
  }

  let state = loadState();

  function saveState() {
    if (!collecting()) return;
    try {
      localStorage.setItem(STATE_KEY, JSON.stringify(state));
    } catch {
      // Storage full or blocked: the profile still gets this page's write.
    }
  }

  window.addEventListener('storage', (event) => {
    if (event.key === STATE_KEY) state = loadState();
  });

  // ---------------------------------------------------------------------------
  // Klaviyo
  // ---------------------------------------------------------------------------

  const email = () => window.KlaviyoQueue?.getKlaviyoIdentifiedEmail() || null;
  // Who the profile writes go to: the email, or else the exchange ID of a shopper who came
  // from a Klaviyo email. klaviyo.js attaches an identify or track without an email to
  // the exchange ID's profile, so the email never has to be on the device.
  const EXCHANGE_PREFIX = 'exchange:';
  function profileKey() {
    const known = email();
    if (known) return known;
    const exchangeId = window.KlaviyoQueue?.getKlaviyoExchangeId?.();
    return exchangeId ? EXCHANGE_PREFIX + exchangeId : null;
  }
  // Same rules as klaviyo-enhanced.liquid: 'pending' until Shopify's Customer Privacy API
  // has loaded, and nothing is sent or stored until it resolves.
  function consentStatus() {
    const privacy = window.Shopify?.customerPrivacy;
    if (typeof privacy?.analyticsProcessingAllowed !== 'function') return 'pending';
    // Profiles feed marketing email, so a marketing decline stops it too.
    const refused =
      privacy.analyticsProcessingAllowed() === false ||
      (typeof privacy.marketingAllowed === 'function' && privacy.marketingAllowed() === false);
    if (!refused) return 'allowed';
    // Where consent is required, "not allowed" also means the banner is still waiting
    // for an answer. That is pending, not a decline.
    return privacy.shouldShowBanner?.() ? 'pending' : 'declined';
  }
  const consented = () => consentStatus() === 'allowed';

  // A declined shopper's activity is not kept on the device either, so it can't reach the
  // profile later if consent flips. Anything stored before the decline is dropped. While
  // consent is pending nothing is collected, and what is stored stays put.
  function collecting() {
    if (consented()) return true;
    if (consentStatus() === 'pending') return false;
    state = {};
    try {
      localStorage.removeItem(STATE_KEY);
    } catch {
      // Storage blocked: nothing was stored.
    }
    return false;
  }

  // On a shared device the next shopper to identify must not inherit the last one's
  // views, wishlist or guides. Activity from before anyone identified goes to whoever
  // identifies first. An exchange-ID owner who then gives an email keeps the state: the
  // exchange ID came from their own email click on this browser.
  function claimState() {
    const known = profileKey();
    if (!known || !collecting()) return;
    if (state.ownerEmail !== known) {
      const upgrade = state.ownerEmail?.startsWith(EXCHANGE_PREFIX) && !known.startsWith(EXCHANGE_PREFIX);
      if (state.ownerEmail && !upgrade) state = {};
      state.ownerEmail = known;
      saveState();
    }
    if (page.loggedIn && !state.ownerLoggedIn) {
      state.ownerLoggedIn = true;
      saveState();
    }
  }

  // State built while a customer was logged in is theirs. Once a page has no customer,
  // they have logged out, and the next person on the device starts clean.
  function forgetLoggedOutCustomer() {
    if (page.loggedIn || !state.ownerLoggedIn || !collecting()) return;
    state = {};
    try {
      localStorage.removeItem(STATE_KEY);
    } catch {
      // Storage blocked: nothing was stored.
    }
  }

  // Sent only to a known shopper; never queued for replay at signup.
  function send(event, props) {
    if (profileKey() && consented()) window.KlaviyoQueue.sendToKlaviyo(event, props, email());
  }

  // Queued while anonymous, replayed at signup by klaviyo-enhanced.liquid.
  function sendOrQueue(event, props) {
    if (consented()) window.KlaviyoQueue?.track(event, props);
  }

  function push(command) {
    window._learnq = window._learnq || [];
    window._learnq.push(command);
  }

  // Today's date in the shop's timezone, worked out when it's needed, so a tab left open
  // past midnight still agrees with the PDP about when a promo ends. utcOffset is the
  // shop's offset from Liquid, e.g. "-0700".
  function shopToday() {
    const match = /^([+-])(\d{2})(\d{2})$/.exec(page.utcOffset || '');
    const minutes = match ? (match[1] === '-' ? -1 : 1) * (Number(match[2]) * 60 + Number(match[3])) : 0;
    return new Date(Date.now() + minutes * 60000).toISOString().slice(0, 10);
  }

  function hasActivePromo(item) {
    return !!(item.Promo && item.Promo.EndDate >= shopToday());
  }

  function activeRoomPreviews() {
    if (!page.roomPreviewsEnabled) return [];
    return (state.roomPreviews || []).filter((preview) => Date.parse(preview.ExpiresAt) > Date.now());
  }

  function withRoomPreviews(items) {
    const previews = activeRoomPreviews();
    if (!previews.length) return items;
    return items.map((item) => {
      const preview = previews.find((p) => p.VariantID === item.VariantID);
      if (!preview) return item;
      return Object.assign({}, item, { RoomPreviewURL: preview.RoomPreviewURL, RoomPreviewExpiresAt: preview.ExpiresAt });
    });
  }

  // Category is Shopify's product.type, a path like "Fans > Fan Accessories > Downrods".
  const topCategory = (item) => (item.Category || '').split('>')[0].trim();
  const isAccessory = (item) => ACCESSORY_PATTERN.test(item.Category || '');
  const mostLinesInOneCategory = (items) => {
    const counts = {};
    // An uncategorized line (a blank product type, or a /cart.js fallback) counts toward
    // no category.
    items.forEach((item) => {
      if (item.Category) counts[item.Category] = (counts[item.Category] || 0) + 1;
    });
    return Math.max(0, ...Object.values(counts));
  };

  // The main item leads every cart email: the highest line that isn't an accessory, or
  // the highest accessory when that's all the cart holds.
  function cartHero(items) {
    const byLinePrice = items.slice().sort((a, b) => b.LinePrice - a.LinePrice);
    return byLinePrice.find((item) => !isAccessory(item)) || byLinePrice[0] || null;
  }

  // Shortlist (the cart used as a wishlist) comes first, as the emails list it. Project
  // goes ahead of quantity, or "10+ of one item" would always read as quantity.
  function cartShape(items, categories, maxQuantity) {
    const lines = items.length;
    if (lines >= 4 && maxQuantity === 1 && mostLinesInOneCategory(items) >= lines * 0.6) return 'shortlist';
    if ((categories.length >= 3 && lines >= 4) || maxQuantity >= 10) return 'project';
    if (maxQuantity >= 2) return 'quantity';
    return 'single';
  }

  function currentCart() {
    const items = withRoomPreviews(state.cart.Items);
    const hero = cartHero(items);
    const categories = unique(items.map(topCategory));
    const maxQuantity = items.reduce((max, item) => Math.max(max, item.Quantity), 0);
    const promoEnds = items.filter(hasActivePromo).map((item) => item.Promo.EndDate).sort()[0] || null;
    return Object.assign({}, state.cart, {
      Items: items,
      Hero: hero,
      Lines: items.length,
      OtherLines: hero ? items.length - 1 : 0,
      // StockType only: in-stock lines can carry a lead time in weeks too, and the email
      // would call them backordered.
      HasBackorder: items.some((item) => /backorder/i.test(item.StockType || '')),
      PromoEnds: promoEnds,
      Shape: items.length ? cartShape(items, categories, maxQuantity) : null,
      MaxQuantity: maxQuantity,
      Categories: categories,
    });
  }

  // Each device keeps the cart values it saw in the last 7 days. The property only moves
  // when the profile is written, so it can sit high until the shopper's next visit.
  function recentCartValues() {
    const since = Date.now() - CART_VALUE_WINDOW;
    return (state.cartValues || []).filter((entry) => Date.parse(entry.At) > since);
  }

  function profileProperties() {
    const props = {};
    const viewed = state.recentlyViewed || [];
    if (viewed.length) {
      props['Recently Viewed'] = withRoomPreviews(viewed);
      props['Last Viewed Brand'] = state.lastViewedBrand || '';
      props['Last Viewed Category'] = state.lastViewedCategory || '';
      props['Recent Brands'] = state.recentBrands || [];
      props['Brand Views'] = state.brandViews || {};
    }

    if (state.cart) {
      const cart = currentCart();
      props.Cart = cart;
      props['Cart Item Count'] = cart.ItemCount;
      props['Cart Value'] = cart.Value;
      props['Cart Brands'] = unique(cart.Items.map((item) => item.Brand));
      props['Cart Has Promo'] = cart.Items.some(hasActivePromo);
      // Segments can't filter on keys inside Cart, so these two stand on their own.
      props['Cart Shape'] = cart.Shape;
      props['Cart Value Max 7d'] = Math.max(cart.Value, ...recentCartValues().map((entry) => entry.Value));
    }

    if (state.wishlist) {
      props.Wishlist = withRoomPreviews(state.wishlist);
      props['Wishlist Count'] = state.wishlistCount;
      props['Wishlist Brands'] = unique(state.wishlist.map((item) => item.Brand));
    }

    const guides = state.guidesRead || [];
    if (guides.length) {
      props['Guide Reader'] = true;
      props['Guides Read Count'] = state.guidesReadCount || guides.length;
      props['Last Guide Read At'] = guides[0].ReadAt;
      props['Guides Read'] = guides;
      props['Guide Categories'] = unique(guides.map((guide) => guide.GuideCategory));
    }

    if (state.tradePageViewedAt) props['Trade Page Viewed At'] = state.tradePageViewedAt;
    // From this page's customer only, and only ever true, so a later visit can't
    // overwrite approval and a stored value can't carry to someone else.
    if (page.tradeApproved) props['Trade Approved'] = true;

    if (page.roomPreviewsEnabled && state.roomPreviews) {
      const previews = activeRoomPreviews();
      props['Room Previews'] = previews;
      props['Room Preview Count'] = previews.length;
      if (previews.length) props['Last Room Preview At'] = previews[0].CreatedAt;
    }

    return props;
  }

  // A short fingerprint of the last payload written, so an unchanged profile (a B2B
  // customer on every page, a resync that found nothing new) is not written again.
  function fingerprint(text) {
    let hash = 5381;
    for (let i = 0; i < text.length; i++) hash = ((hash << 5) + hash + text.charCodeAt(i)) | 0;
    return text.length + ':' + hash;
  }

  function writeProfile() {
    const known = profileKey();
    if (!known || !consented()) return;
    claimState();
    const props = profileProperties();
    if (!Object.keys(props).length) return;

    // Keyed by who it went to, so the same data is written again once an exchange-ID
    // shopper gives their email.
    const print = fingerprint(known + '|' + JSON.stringify(props));
    if (print === state.lastProfileWrite) return;
    state.lastProfileWrite = print;
    saveState();
    const address = email();
    push(['identify', Object.assign(address ? { $email: address } : {}, props)]);
  }

  // ---------------------------------------------------------------------------
  // Variant data from the klaviyo-tracking-data section
  // ---------------------------------------------------------------------------

  const variantCache = {};
  if (page.product?.selected) variantCache[page.product.selected.VariantID] = page.product.selected;

  async function fetchSection(path) {
    const separator = path.includes('?') ? '&' : '?';
    const response = await fetch(path + separator + 'section_id=' + SECTION_ID);
    if (!response.ok) throw new Error('section ' + response.status);
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return JSON.parse(doc.querySelector('[data-klaviyo-tracking]').textContent);
  }

  // The object for any variant, by product URL or handle. Rendered on the server, so it
  // carries the variant's own brand and promo, and works past the 250 variants Liquid
  // lists on a product.
  async function variantItem(productPath, variantId) {
    if (variantId && variantCache[variantId]) return variantCache[variantId];
    try {
      const item = await fetchSection(productPath + (variantId ? '?variant=' + variantId : ''));
      variantCache[item.VariantID] = item;
      return item;
    } catch {
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // Product views: one Viewed Product per page view
  // ---------------------------------------------------------------------------

  let currentVariantId = page.product?.selected?.VariantID || null;
  let viewSent = false;
  let lastViewedVariantId = null;

  // Trigger says why it was sent: page_view for the page's first, variant_change for
  // each variant the shopper moves to after it. Browse flows should filter on page_view.
  function viewedProductProps(item, trigger) {
    return Object.assign({}, withRoomPreviews([item])[0], {
      Categories: page.product.categories || [],
      Trigger: trigger,
      // The Klaviyo app embed sends its own Viewed Product; browse flows filter on this.
      Source: 'ldc-theme',
      is_on_sale: !!(item.CompareAtPrice && item.CompareAtPrice > item.Price),
      is_available: item.Available,
    });
  }

  // A new view goes to the front of Recently Viewed and counts toward its brand. A variant
  // change after that replaces the product's entry where it stands.
  function recordView(item, isNewView) {
    if (!collecting()) return;
    claimState();
    // A copy: earlier identify payloads still queued in _learnq hold the old array.
    const viewed = (state.recentlyViewed || []).slice();
    const index = viewed.findIndex((v) => v.ProductID === item.ProductID);

    if (isNewView) {
      const others = viewed.filter((v) => v.ProductID !== item.ProductID);
      state.recentlyViewed = [Object.assign({}, item, { ViewedAt: now() })].concat(others).slice(0, RECENTLY_VIEWED_MAX);

      if (item.Brand) {
        state.brandViews = Object.assign({}, state.brandViews);
        state.brandViews[item.Brand] = (state.brandViews[item.Brand] || 0) + 1;
        state.lastViewedBrand = item.Brand;
        state.recentBrands = unique([item.Brand].concat(state.recentBrands || [])).slice(0, RECENT_BRANDS_MAX);
      }
      if (item.Category) state.lastViewedCategory = item.Category;
    } else if (index !== -1) {
      viewed[index] = Object.assign({}, item, { ViewedAt: viewed[index].ViewedAt });
      state.recentlyViewed = viewed;
    } else {
      return;
    }

    saveState();
    writeProfile();
  }

  async function viewedProduct() {
    if (!collecting()) return;
    const item = await variantItem(page.product.url, currentVariantId);
    if (!item) return;
    viewSent = true;
    lastViewedVariantId = item.VariantID;

    const categories = page.product.categories || [];
    // Queued for an anonymous shopper, and replayed with its own time at signup.
    sendOrQueue('Viewed Product', viewedProductProps(item, 'page_view'));

    if (profileKey() && consented()) {
      push([
        'trackViewedItem',
        {
          Title: item.ProductName,
          ItemId: item.ProductID,
          Categories: categories,
          ImageUrl: item.ImageURL,
          Url: item.ProductURL,
          Metadata: { Brand: item.Brand, Price: item.Price, CompareAtPrice: item.CompareAtPrice },
        },
      ]);
    }

    recordView(item, true);
  }

  // Swatch clicks arrive in bursts; only the variant the shopper settles on is sent.
  async function variantSettled(variant) {
    if (!collecting()) return;
    const variantId = String(variant.id);
    const item = await variantItem(page.product.url, variantId);
    if (variantId !== currentVariantId) return;

    if (tracking.variantSelected) {
      const base = item || {
        ProductID: String(variant.product_id || page.product.selected?.ProductID || ''),
        VariantID: variantId,
        SKU: variant.sku || '',
        Price: variant.price / 100,
      };
      // A single-variant product's only variant is titled "Default Title"; it has no option.
      const optionValue = item ? item.VariantName : variant.title || variant.name || '';
      send(
        'Variant Selected',
        Object.assign({}, base, {
          option_value: optionValue === 'Default Title' ? '' : optionValue,
          is_available: variant.available !== undefined ? variant.available : base.Available,
        })
      );
    }

    if (viewSent && item && item.VariantID !== lastViewedVariantId) {
      lastViewedVariantId = item.VariantID;
      if (tracking.viewedProduct) sendOrQueue('Viewed Product', viewedProductProps(item, 'variant_change'));
      recordView(item, false);
    }
  }

  function setupProduct() {
    if (!page.product) return;
    if (tracking.viewedProduct) setTimeout(viewedProduct, VIEWED_PRODUCT_DELAY);

    let timer = null;
    const changed = (variant) => {
      if (!variant) return;
      currentVariantId = String(variant.id);
      clearTimeout(timer);
      timer = setTimeout(() => variantSettled(variant), VARIANT_CHANGE_DELAY);
    };
    // The classic product form publishes variantChange. The V2 selector, used by the
    // restructured PDP, dispatches only v2:variantChange. If both fire, the delay above
    // makes them one change.
    if (window.FoxTheme?.pubsub) {
      FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => changed(event?.data?.variant));
    }
    document.addEventListener('v2:variantChange', (event) => changed(event.detail?.variant));
  }

  // ---------------------------------------------------------------------------
  // Cart
  // ---------------------------------------------------------------------------

  // Titles read "Brand - SKU - description"; without the section's ShortName, the SKU
  // segment is dropped.
  function fallbackShortName(title) {
    const parts = (title || '').split(' - ');
    return parts.length > 2 ? parts[0] + ' ' + parts.slice(2).join(' - ') : title;
  }

  function buildCart(cart, rendered) {
    const lines = {};
    (rendered || []).forEach((item) => {
      lines[item.VariantID] = item;
    });

    const items = (cart.items || []).map((line) => {
      const variantId = String(line.variant_id);
      const item = lines[variantId] || {
        ProductID: String(line.product_id),
        VariantID: variantId,
        SKU: line.sku || '',
        ProductName: line.product_title,
        ShortName: fallbackShortName(line.product_title),
        VariantName: line.variant_title || '',
        Price: line.final_price / 100,
        ImageURL: line.image || '',
        ProductURL: window.location.origin + line.url,
      };
      return Object.assign({}, item, { Quantity: line.quantity, LinePrice: line.final_line_price / 100 });
    });

    const permalink = items.map((item) => item.VariantID + ':' + item.Quantity).join(',');
    return {
      UpdatedAt: now(),
      ItemCount: cart.item_count,
      Locale: page.locale || '',
      Value: cart.total_price / 100,
      Currency: cart.currency,
      // ?storefront=true rebuilds the cart; without it Shopify skips to checkout.
      CartURL: permalink
        ? window.location.origin + rootUrl + 'cart/' + permalink + '?storefront=true'
        : window.location.origin + rootUrl + 'cart',
      Items: items,
    };
  }

  function cartSignature(cart) {
    const lines = (cart.items || []).map((line) => line.variant_id + ':' + line.quantity).sort();
    return lines.join(',') + '|' + cart.total_price;
  }

  // theme.js publishes cartUpdate on every page load to hydrate the header count, so only
  // a cart that differs from the last one sent counts as a change. The cart is never
  // deleted from the profile: an empty cart writes ItemCount 0.
  // A full cart published with cartUpdate (the header hydrate, cart.js after a change) is
  // used as is; /cart.js is fetched only when the event carried none.
  // One snapshot at a time: signup and the page-load hydrate can both ask within a
  // second, and each must see the signature the other wrote.
  let snapshotChain = Promise.resolve();
  function snapshotCart(published) {
    snapshotChain = snapshotChain.then(() => takeSnapshot(published)).catch(() => {});
    return snapshotChain;
  }

  async function takeSnapshot(published) {
    if (!profileKey() || !collecting()) return;
    let cart = published;
    if (!cart) {
      try {
        const response = await fetch(rootUrl + 'cart.js', { headers: { Accept: 'application/json' } });
        cart = await response.json();
      } catch {
        return;
      }
    }

    claimState();
    const signature = cartSignature(cart);
    // A cart stored before ShortName existed is rebuilt once, even when it hasn't changed.
    const current = state.cart?.Items?.every((item) => 'ShortName' in item);
    if (signature === state.cartSignature && current) return;

    let rendered = [];
    if (cart.item_count > 0) {
      try {
        rendered = await fetchSection(rootUrl + 'cart');
      } catch {
        // The /cart.js fields in buildCart stand in for any line the section didn't render.
      }
    }

    state.cartSignature = signature;
    state.cart = buildCart(cart, rendered);
    state.cartValues = recentCartValues().concat({ At: state.cart.UpdatedAt, Value: state.cart.Value });
    saveState();
    writeProfile();
    if (state.cart.ItemCount > 0) {
      const snapshot = currentCart();
      send('LDC - Cart Snapshot', Object.assign({ $value: snapshot.Value }, snapshot));
    }
  }

  function setupCart() {
    if (!tracking.cartUpdated || !window.FoxTheme?.pubsub) return;
    let timer = null;
    FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
      const cart = event?.cart;
      const complete =
        cart && Array.isArray(cart.items) && typeof cart.total_price === 'number' && typeof cart.item_count === 'number';
      clearTimeout(timer);
      timer = setTimeout(() => snapshotCart(complete ? cart : null), CART_SNAPSHOT_DELAY);
    });
  }

  // ---------------------------------------------------------------------------
  // Wishlist: the profile mirrors WishlistKing's whole list, not just this device's saves
  // ---------------------------------------------------------------------------

  function readWishlistKing() {
    const wk = window.WishlistKing;
    if (!wk?.state?.observeWishlist) return Promise.resolve(null);
    return new Promise((resolve) => {
      const handle = {};
      let settled = false;
      const finish = (value) => {
        if (settled) return;
        settled = true;
        handle.subscription?.unsubscribe();
        resolve(value);
      };
      setTimeout(() => finish(null), 3000);
      // An observable holding cached state can emit during subscribe() itself.
      handle.subscription = wk.state.observeWishlist({ wishlistId: 'mine' }).subscribe(finish);
      if (settled) handle.subscription?.unsubscribe();
    });
  }

  // Each unknown item is a section render on Shopify. A first sync can hold 50 of them,
  // so only a few run at once.
  async function inBatches(list, work, size = SECTION_FETCHES_AT_ONCE) {
    const results = [];
    for (let i = 0; i < list.length; i += size) {
      results.push(...(await Promise.all(list.slice(i, i + size).map(work))));
    }
    return results;
  }

  // Saves the shopper just made on this page, from wlk:wishlist:added. On the device's
  // first sync every listed item looks pre-existing, so these say which ones are new.
  let pendingAdds = [];

  function isPendingAdd(item) {
    return pendingAdds.some((add) =>
      add.variantId ? add.variantId === item.VariantID : add.handle === item.ProductHandle
    );
  }

  // Saves made before this device first saw the list have no known SavedAt or
  // PriceWhenSaved; WishlistKing records neither.
  async function syncWishlist() {
    if (!tracking.wishlist || !collecting()) return;
    const wishlist = await readWishlistKing();
    if (!wishlist || !Array.isArray(wishlist.items) || !collecting()) return;

    claimState();
    const previous = {};
    (state.wishlist || []).forEach((item) => {
      previous[item.VariantID] = item;
    });
    const firstSync = !state.wishlist;

    const entries = wishlist.items.slice(0, WISHLIST_MAX);
    const resolved = await inBatches(
      entries,
      (entry) => {
        const variantId = String(entry.variant?.id || entry.variantId || '');
        if (variantId && previous[variantId]) return previous[variantId];
        const handle = entry.product?.handle;
        if (!handle) return null;
        return variantItem(rootUrl + 'products/' + handle, variantId).then((item) => {
          if (!item) return null;
          const known = previous[item.VariantID];
          if (known) return known;
          const isNew = !firstSync || isPendingAdd(item);
          return Object.assign({}, item, {
            SavedAt: isNew ? now() : null,
            PriceWhenSaved: isNew ? item.Price : null,
          });
        });
      }
    );

    const items = resolved.filter(Boolean);
    const added = items.filter((item) => !previous[item.VariantID] && (!firstSync || isPendingAdd(item)));
    pendingAdds = [];
    state.wishlist = items;
    state.wishlistCount = wishlist.items.length;
    saveState();

    added.forEach((item) => {
      const event = Object.assign({}, item);
      delete event.SavedAt;
      delete event.PriceWhenSaved;
      sendOrQueue('LDC - Added to Wishlist', event);
    });
    writeProfile();
  }

  function setupWishlist() {
    if (!tracking.wishlist) return;
    let timer = null;
    const schedule = (event) => {
      if (event.type === 'wlk:wishlist:added') {
        const { productHandle, variantId } = event.detail || {};
        pendingAdds.push({ handle: productHandle || '', variantId: variantId ? String(variantId) : '' });
      }
      clearTimeout(timer);
      timer = setTimeout(syncWishlist, WISHLIST_SYNC_DELAY);
    };
    ['wlk:wishlist:added', 'wlk:wishlist:removed', 'wlk:wishlist:updated'].forEach((name) =>
      document.addEventListener(name, schedule)
    );
  }

  // ---------------------------------------------------------------------------
  // Room previews: only with the theme setting on and the shopper's marketing opt-in
  // ---------------------------------------------------------------------------

  function onRoomGenerated(event) {
    const detail = event.detail || {};
    if (!page.roomPreviewsEnabled || detail.marketingOptIn !== true || !detail.imageUrl || !collecting()) return;

    claimState();
    const created = Date.now();
    const preview = {
      PreviewID: String(detail.previewId || ''),
      JobID: String(detail.jobId || ''),
      ProductID: String(detail.product?.id || ''),
      VariantID: String(detail.product?.variantId || ''),
      ProductName: detail.product?.title || '',
      RoomPreviewURL: detail.imageUrl,
      CreatedAt: new Date(created).toISOString(),
      ExpiresAt: new Date(created + ROOM_PREVIEW_LIFETIME).toISOString(),
    };
    state.roomPreviews = [preview]
      .concat(activeRoomPreviews().filter((p) => p.PreviewID !== preview.PreviewID))
      .slice(0, ROOM_PREVIEWS_MAX);
    saveState();
    writeProfile();
  }

  function onRoomUpdated(event) {
    const deleted = event.detail?.deleted;
    if (!page.roomPreviewsEnabled || !deleted || !collecting()) return;

    claimState();
    const previews = state.roomPreviews || [];
    const remaining = previews.filter((p) => p.PreviewID !== String(deleted));
    if (remaining.length === previews.length) return;
    state.roomPreviews = remaining;
    saveState();
    writeProfile();
  }

  function setupRoomPreviews() {
    if (!page.roomPreviewsEnabled) return;
    document.addEventListener('room:generated', onRoomGenerated);
    document.addEventListener('room:updated', onRoomUpdated);
  }

  // ---------------------------------------------------------------------------
  // Buying guides and trade program
  // ---------------------------------------------------------------------------

  function setupGuide() {
    const guide = page.guide;
    if (!guide) return;

    let done = false;
    let timer = null;

    function read() {
      if (done) return;
      done = true;
      clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
      if (!collecting()) return;

      claimState();
      const guides = state.guidesRead || [];
      if (!guides.some((g) => g.GuideHandle === guide.GuideHandle)) {
        state.guidesReadCount = (state.guidesReadCount || 0) + 1;
      }
      state.guidesRead = [
        {
          GuideHandle: guide.GuideHandle,
          GuideTitle: guide.GuideTitle,
          GuideCategory: guide.GuideCategory,
          ReadAt: now(),
        },
      ]
        .concat(guides.filter((g) => g.GuideHandle !== guide.GuideHandle))
        .slice(0, GUIDES_READ_MAX);
      saveState();

      sendOrQueue('LDC - Read Buying Guide', guide);
      writeProfile();
    }

    // Half of what can be scrolled, so a guide shorter than two screens isn't "read" on
    // arrival.
    function onScroll() {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable > 0 && window.scrollY >= scrollable / 2) read();
    }

    // 30 seconds of the guide on screen; time in a background tab doesn't count.
    let remaining = GUIDE_READ_DELAY;
    let startedAt = 0;
    function startTimer() {
      if (done || document.hidden) return;
      startedAt = Date.now();
      timer = setTimeout(read, remaining);
    }
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        clearTimeout(timer);
        if (startedAt) remaining = Math.max(0, remaining - (Date.now() - startedAt));
        startedAt = 0;
      } else {
        startTimer();
      }
    });

    startTimer();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  function setupTrade() {
    if (!collecting()) return;
    if (page.isTradePage) {
      claimState();
      state.tradePageViewedAt = now();
      saveState();
      sendOrQueue('LDC - Viewed Trade Program', { PageURL: window.location.origin + window.location.pathname });
    }
    if (page.isTradePage || page.tradeApproved) writeProfile();
  }

  // ---------------------------------------------------------------------------
  // Signup: write everything gathered while anonymous, and the cart as it is now
  // ---------------------------------------------------------------------------

  function syncIdentity() {
    const known = profileKey();
    if (!known || !collecting()) return;
    claimState();
    if (state.identifiedEmail === known) return;
    state.identifiedEmail = known;
    saveState();
    writeProfile();
    if (tracking.cartUpdated) snapshotCart();
    syncWishlist();
  }

  // ---------------------------------------------------------------------------

  let started = false;
  function start() {
    if (started) return;
    started = true;
    collecting();
    forgetLoggedOutCustomer();
    syncIdentity();
    setupProduct();
    setupCart();
    setupWishlist();
    setupRoomPreviews();
    setupGuide();
    setupTrade();
    document.addEventListener('klaviyo-enhanced:identified', syncIdentity);
  }

  if (window.KlaviyoEnhanced?.initialized) {
    start();
  } else {
    document.addEventListener('klaviyo-enhanced:ready', start, { once: true });
  }
})();
